import { memo } from 'react';
import { TextNode } from './TextNode';
import { ImageNode } from './ImageNode';
import { VideoNode } from './VideoNode';
import { AudioNode } from './AudioNode';
import { GenerateImageNode } from './GenerateImageNode';
import { GenerateVideoNode } from './GenerateVideoNode';
import { ImageGroupNode } from './ImageGroupNode';
import { VideoGroupNode } from './VideoGroupNode';
import { LLMNode } from './LLMNode';
import { AgentNode } from './AgentNode';
import { GroupNode } from './GroupNode';
import { StyleCardNode } from './StyleCardNode';
import type {
  AgentNodeData,
  AudioNodeData,
  CanvasNode,
  GenerateImageNodeData,
  GenerateVideoNodeData,
  GroupNodeData,
  ImageGroupNodeData,
  ImageNodeData,
  LlmNodeData,
  StyleCardNodeData,
  TextNodeData,
  VideoGroupNodeData,
  VideoNodeData,
} from '../types';

// 节点组件 memo 化：boardStore 为不可变更新，未变更节点 props 引用不变，
// 大画布下拖动 / 编辑单个节点时避免整棵树全量重渲染
const MemoTextNode = memo(TextNode);
const MemoImageNode = memo(ImageNode);
const MemoVideoNode = memo(VideoNode);
const MemoAudioNode = memo(AudioNode);
const MemoGenerateImageNode = memo(GenerateImageNode);
const MemoGenerateVideoNode = memo(GenerateVideoNode);
const MemoImageGroupNode = memo(ImageGroupNode);
const MemoVideoGroupNode = memo(VideoGroupNode);
const MemoLLMNode = memo(LLMNode);
const MemoAgentNode = memo(AgentNode);
const MemoGroupNode = memo(GroupNode);
const MemoStyleCardNode = memo(StyleCardNode);

interface NodeRendererProps {
  nodes: CanvasNode[];
  selectedIds: string[];
  highlightedIds: string[];
  userCredits?: number;
}

/**
 * 节点分发渲染：根据 node.type 选用对应的 memo 节点组件。
 * 分组容器排在最前（先渲染，位于成员节点之下）。
 */
export function NodeRenderer({ nodes, selectedIds, highlightedIds, userCredits }: NodeRendererProps) {
  const groups = nodes.filter((n) => n.type === 'group');
  const others = nodes.filter((n) => n.type !== 'group');
  return (
    <>
      {[...groups, ...others].map((n) => {
        const selected = selectedIds.includes(n.id);
        const highlighted = highlightedIds.includes(n.id) && !selected;
        switch (n.type) {
          case 'text':
            return <MemoTextNode key={n.id} node={n as CanvasNode<TextNodeData>} selected={selected} highlighted={highlighted} />;
          case 'image':
            return <MemoImageNode key={n.id} node={n as CanvasNode<ImageNodeData>} selected={selected} highlighted={highlighted} />;
          case 'video':
            return <MemoVideoNode key={n.id} node={n as CanvasNode<VideoNodeData>} selected={selected} highlighted={highlighted} />;
          case 'audio':
            return <MemoAudioNode key={n.id} node={n as CanvasNode<AudioNodeData>} selected={selected} highlighted={highlighted} />;
          case 'generate_image':
            return (
              <MemoGenerateImageNode
                key={n.id}
                node={n as CanvasNode<GenerateImageNodeData>}
                selected={selected}
                highlighted={highlighted}
                userCredits={userCredits}
              />
            );
          case 'generate_video':
            return (
              <MemoGenerateVideoNode
                key={n.id}
                node={n as CanvasNode<GenerateVideoNodeData>}
                selected={selected}
                highlighted={highlighted}
                userCredits={userCredits}
              />
            );
          case 'image_group':
            return <MemoImageGroupNode key={n.id} node={n as CanvasNode<ImageGroupNodeData>} selected={selected} highlighted={highlighted} />;
          case 'video_group':
            return <MemoVideoGroupNode key={n.id} node={n as CanvasNode<VideoGroupNodeData>} selected={selected} highlighted={highlighted} />;
          case 'llm':
            return <MemoLLMNode key={n.id} node={n as CanvasNode<LlmNodeData>} selected={selected} highlighted={highlighted} />;
          case 'agent':
            return <MemoAgentNode key={n.id} node={n as CanvasNode<AgentNodeData>} selected={selected} highlighted={highlighted} />;
          case 'style_card':
            return <MemoStyleCardNode key={n.id} node={n as CanvasNode<StyleCardNodeData>} selected={selected} highlighted={highlighted} />;
          case 'group':
            return <MemoGroupNode key={n.id} node={n as CanvasNode<GroupNodeData>} selected={selected} highlighted={highlighted} />;
          default:
            return null;
        }
      })}
    </>
  );
}